import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FiClipboard, FiBox, FiCalendar, FiCheckCircle, FiBarChart2,
  FiSmartphone, FiArrowRight, FiDownload, FiCheck, FiStar,
  FiExternalLink, FiAlertTriangle, FiUsers, FiPackage,
  FiShield, FiLayers, FiEdit3, FiPlayCircle,
  FiTrendingDown, FiDollarSign, FiServer, FiZap, FiGitMerge, FiLock,
  FiBell, FiClock, FiTrendingUp,
} from 'react-icons/fi';
import Logo from '../components/Logo';
import { useSite } from '../context/SiteContext';
import { useAuth } from '../context/AuthContext';
import { APP_VERSION, COPYRIGHT_YEAR } from '../version';

// Brand identity is fixed on the marketing page — does NOT inherit the
// tenant's site_name. Mikes App Hub is the parent platform attribution.
const BRAND_NAME = 'Asset Logic';
const PARENT_NAME = 'Mikes App Hub';
const PARENT_URL = 'https://mikesapphub.com';

// ────────────────────────────────────────────────────────────────────────────
// Animated background — three gradient orbs + floating particles. Reused
// verbatim from the previous design; it sets the mood without competing
// with content.
// ────────────────────────────────────────────────────────────────────────────
function SlideBackground() {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 0, background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 35%, #1e40af 65%, #7c3aed 100%)' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      <motion.div animate={{ x: [0, 60, 0], y: [0, -40, 0], scale: [1, 1.3, 1] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        style={{ position: 'absolute', top: '5%', left: '5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      <motion.div animate={{ x: [0, -50, 0], y: [0, 50, 0], scale: [1, 1.2, 1] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        style={{ position: 'absolute', top: '40%', right: '0%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      <motion.div animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut', delay: 6 }}
        style={{ position: 'absolute', bottom: '5%', left: '25%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      {[...Array(12)].map((_, i) => (
        <motion.div key={i}
          animate={{ y: [0, -(window.innerHeight + 100)], opacity: [0, 0.5, 0] }}
          transition={{ duration: 10 + Math.random() * 8, repeat: Infinity, delay: i * 1.2, ease: 'linear' }}
          style={{ position: 'absolute', bottom: -10, left: `${(i / 12) * 100}%`, width: 3 + Math.random() * 4, height: 3 + Math.random() * 4, borderRadius: '50%', background: i % 3 === 0 ? 'rgba(59,130,246,0.3)' : i % 3 === 1 ? 'rgba(139,92,246,0.25)' : 'rgba(16,185,129,0.25)' }} />
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Counter that ramps from 0 to a target value when scrolled into view.
// ────────────────────────────────────────────────────────────────────────────
function Counter({ to, suffix = '', duration = 1.2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

// ────────────────────────────────────────────────────────────────────────────
// Stylised dashboard mock — the visual centrepiece. All numbers are fake but
// plausible. Pure HTML/CSS so it renders instantly without a backend.
// ────────────────────────────────────────────────────────────────────────────
function DashboardMock() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const bars = [
    { label: 'PM01', val: 24, color: '#2563eb' },
    { label: 'PM03', val: 11, color: '#10b981' },
    { label: 'Sub-WOs', val: 7, color: '#f59e0b' },
  ];
  const max = Math.max(...bars.map(b => b.val));
  const activity = [
    { icon: '✅', text: 'WO 410001077 TECO’d', meta: 'by Mike · just now', color: '#10b981' },
    { icon: '📋', text: 'Material ordered — Hydraulic Hose', meta: 'PO 4501 · 2m ago', color: '#2563eb' },
    { icon: '📬', text: 'Compressor delivered', meta: 'awaiting install · 8m ago', color: '#f59e0b' },
  ];
  return (
    <div ref={ref} style={{
      borderRadius: 16, overflow: 'hidden',
      background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(255,255,255,0.1)',
      boxShadow: '0 30px 80px rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)',
    }}>
      {/* faux browser chrome */}
      <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.25)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }} />
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981' }} />
        <span style={{ marginLeft: 14, fontSize: 11, color: 'rgba(255,255,255,0.45)', fontFamily: 'monospace' }}>assetlogic.app/dashboard</span>
      </div>

      <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20 }}>
        {/* Left column — KPIs + bar chart */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { label: 'Open WOs', value: 42, color: '#3b82f6' },
              { label: 'Hours Logged', value: 312, color: '#10b981', suffix: 'h' },
              { label: 'On-Time', value: 94, color: '#f59e0b', suffix: '%' },
            ].map((k) => (
              <div key={k.label} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: k.color, lineHeight: 1 }}>
                  <Counter to={k.value} suffix={k.suffix || ''} />
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 4 }}>{k.label}</div>
              </div>
            ))}
          </div>

          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginBottom: 12, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Remaining work by type</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {bars.map((b, i) => (
                <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', width: 64 }}>{b.label}</span>
                  <div style={{ flex: 1, height: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 5, overflow: 'hidden' }}>
                    <motion.div initial={{ width: 0 }} animate={inView ? { width: `${(b.val / max) * 100}%` } : { width: 0 }}
                      transition={{ duration: 1, delay: 0.2 + i * 0.15, ease: 'easeOut' }}
                      style={{ height: '100%', background: b.color, borderRadius: 5 }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: b.color, width: 24, textAlign: 'right' }}>{b.val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* gauge */}
          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ position: 'relative', width: 64, height: 64 }}>
              <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
                <motion.circle cx="18" cy="18" r="15" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round"
                  initial={{ strokeDasharray: '0 94' }}
                  animate={inView ? { strokeDasharray: `${0.87 * 94} 94` } : { strokeDasharray: '0 94' }}
                  transition={{ duration: 1.4, ease: 'easeOut' }} />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#10b981' }}>
                <Counter to={87} suffix="%" />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Assets</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>116 of 134 in service</div>
            </div>
          </div>
        </div>

        {/* Right column — activity feed */}
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginBottom: 12, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Recent activity</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activity.map((a, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: 20 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
                transition={{ delay: 0.5 + i * 0.18, duration: 0.5 }}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: `1px solid ${a.color}30` }}>
                <span style={{ fontSize: 16, lineHeight: 1 }}>{a.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.text}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{a.meta}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const site = useSite();
  const { user } = useAuth();
  const [mobile, setMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Six benefit cards — outcomes, not feature lists.
  const benefits = [
    { icon: FiCalendar, title: 'Plan',     text: 'Drag-and-drop weekly pre-read with capacity bars per technician.' },
    { icon: FiBox,      title: 'Track',    text: 'Every asset with status, cost, history and a full audit trail.' },
    { icon: FiClipboard,title: 'Schedule', text: 'PM01, PM02 and PM03 work orders with templates and recurring jobs.' },
    { icon: FiCheckCircle, title: 'Confirm', text: 'One-tap time confirmations with adherence and variance reporting.' },
    { icon: FiBarChart2, title: 'Report',  text: 'Live dashboard, monthly trends, cost breakdowns, PDF pre-reads.' },
    { icon: FiSmartphone, title: 'Mobile', text: 'Take photos, log time, and TECO from the workshop floor on Android.' },
  ];

  const reviews = [
    { name: 'James Mitchell', role: 'Maintenance Manager', company: 'Pacific Steel Industries', text: 'Asset Logic transformed how we handle preventive maintenance. We reduced equipment downtime by 40% in the first quarter alone.' },
    { name: 'Sarah Chen', role: 'Operations Director', company: 'Greenfield Mining Corp', text: 'The supervisor panel and approval workflows give us complete visibility over our 200+ assets. Our team actually enjoys using it.' },
    { name: 'David Thompson', role: 'Facilities Coordinator', company: 'Harbour City Council', text: 'We went from spreadsheets to Asset Logic in two weeks. The mobile app means our field crew can log work orders on-site.' },
    { name: 'Lisa Patel', role: 'Reliability Engineer', company: 'Southern Cross Logistics', text: 'Migrated from SAP PM for our smaller sites and haven’t looked back. It costs a fraction of what we were paying.' },
  ];

  const ctaPrimary = user ? '/' : '/login';

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#fff' }}>
      <SlideBackground />

      {/* Top bar — solid, blurred, pinned to the top while scrolling */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: mobile ? '10px 16px' : '14px 32px',
        background: 'rgba(8,12,22,0.85)',
        backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.35)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {site?.logoUrl ? <img src={site.logoUrl} alt="" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain' }} /> : <Logo size={32} light />}
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px' }}>{BRAND_NAME}</span>
            <span style={{ fontSize: 9.5, fontWeight: 500, color: 'rgba(255,255,255,0.55)', marginTop: 2, letterSpacing: '0.2px' }}>
              Powered by <a href={PARENT_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontWeight: 600 }}>{PARENT_NAME}</a>
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <a href={PARENT_URL} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
            {PARENT_NAME} <FiExternalLink size={11} />
          </a>
          {!mobile && (
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={() => window.location.href = '/api/download/android'}
              style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiDownload size={12} /> Android
            </motion.button>
          )}
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
            onClick={() => navigate(ctaPrimary)}
            style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 10px rgba(37,99,235,0.45)' }}>
            {user ? 'Open App' : 'Sign in'} <FiArrowRight size={12} />
          </motion.button>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ position: 'relative', zIndex: 10 }}>
        {/* ── HERO ───────────────────────────────────────────────────── */}
        <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: mobile ? '90px 16px 40px' : '120px 32px 40px' }}>
          <div style={{ maxWidth: 980, textAlign: 'center' }}>
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', fontSize: 12, fontWeight: 700, marginBottom: 28 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 12px #10b981' }} />
              Built for maintenance teams that want their day back
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              style={{
                fontSize: mobile ? 48 : 84, fontWeight: 900, letterSpacing: '-2px', lineHeight: 1.02,
                margin: '0 0 8px',
                background: 'linear-gradient(120deg, #ffffff 0%, #cfe1ff 35%, #c9b6ff 70%, #ffffff 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                backgroundClip: 'text', backgroundSize: '200% 100%',
              }}>
              {BRAND_NAME}
            </motion.h1>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.25 }}
              style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 28, fontWeight: 500 }}>
              Powered by <a href={PARENT_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', borderBottom: '1px dotted rgba(255,255,255,0.4)' }}>{PARENT_NAME}</a>
            </motion.div>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
              style={{ fontSize: mobile ? 17 : 22, color: 'rgba(255,255,255,0.78)', maxWidth: 720, margin: '0 auto 36px', lineHeight: 1.5, fontWeight: 400 }}>
              Plan the week, track every asset, and sign off the work — all from one place. The work-order software your supervisors actually open every morning.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
              style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                onClick={() => navigate(ctaPrimary)}
                style={{ padding: '14px 32px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 10px 30px rgba(37,99,235,0.45)' }}>
                {user ? 'Open App' : 'Start free trial'} <FiArrowRight size={16} />
              </motion.button>
              <motion.a whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                href="#tour"
                style={{ padding: '14px 28px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', backdropFilter: 'blur(8px)' }}>
                See it in action
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* ── PRODUCT SHOWCASE ──────────────────────────────────────── */}
        <section id="tour" style={{ padding: mobile ? '36px 16px' : '56px 32px' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto', width: '100%', display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1.2fr 1fr', gap: mobile ? 28 : 60, alignItems: 'center' }}>
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }}>
              <DashboardMock />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: 0.15 }}>
              <h2 style={{ fontSize: mobile ? 32 : 44, fontWeight: 900, letterSpacing: '-1px', margin: '0 0 16px', lineHeight: 1.1 }}>
                One screen. The whole shop.
              </h2>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', margin: '0 0 28px', lineHeight: 1.55 }}>
                The dashboard everyone opens at 7&nbsp;am — remaining work, hours logged, asset availability, and what just happened on the floor. Live, no refresh needed.
              </p>
              {[
                { title: 'See every asset', text: 'Active assets gauge, fleet-wide trend, and a year of monthly history at a glance.' },
                { title: 'Plan the week in seconds', text: 'Drag work orders into days, watch capacity bars fill per technician, freeze a PDF.' },
                { title: 'Sign off with one tap', text: 'Final time confirmations, materials checks, TECO approvals — without leaving the row.' },
              ].map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
                  <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: 8, background: 'rgba(37,99,235,0.18)', border: '1px solid rgba(37,99,235,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6', fontWeight: 800, fontSize: 12 }}>{i + 1}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{b.title}</div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{b.text}</div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── ABOUT / DESCRIPTION ───────────────────────────────────── */}
        <section style={{ padding: mobile ? '40px 16px' : '64px 32px' }}>
          <div style={{ maxWidth: 880, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', marginBottom: 36 }}>
              <div style={{ display: 'inline-block', padding: '5px 12px', borderRadius: 999, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: '#60a5fa', fontSize: 11, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 18 }}>
                What is {BRAND_NAME}?
              </div>
              <h2 style={{ fontSize: mobile ? 30 : 42, fontWeight: 900, letterSpacing: '-1px', margin: '0 0 20px', lineHeight: 1.15 }}>
                A modern CMMS built around how maintenance teams actually work.
              </h2>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, delay: 0.1 }}
              style={{ fontSize: mobile ? 15 : 17, color: 'rgba(255,255,255,0.78)', lineHeight: 1.7, fontWeight: 400 }}>
              <p style={{ margin: '0 0 18px' }}>
                {BRAND_NAME} is a computerised maintenance management system for plants, fleets, councils and facilities — anyone whose day starts with a list of work orders and ends with someone signing off the work. It replaces the spreadsheet trio of <em>planner</em>, <em>tracker</em> and <em>handover sheet</em> with one place that knows about your assets, your team, and what needs doing this week.
              </p>
              <p style={{ margin: '0 0 18px' }}>
                Supervisors plan the week with drag-and-drop, watch capacity bars fill per technician, and freeze a PDF pre-read for the toolbox meeting. Technicians open work orders on Android, take photos straight from the field, log time on the row, and TECO without waiting to get back to the office. Managers see the live dashboard — remaining work, asset availability, monthly trends — without a single spreadsheet refresh.
              </p>
              <p style={{ margin: 0 }}>
                Under the hood it speaks the language of plant maintenance: PM01, PM02, PM03 work order types, sub-WOs and operations, materials with PO tracking, sequence-mode templates, and TECO sign-off. Multi-tenant out of the box, mobile-first by design, and priced to scale from a five-person workshop to a multi-site fleet.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── BUSINESS OUTCOMES — why it makes life & business easier ─── */}
        <section style={{ padding: mobile ? '36px 16px' : '56px 32px' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{ display: 'inline-block', padding: '5px 12px', borderRadius: 999, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', color: '#34d399', fontSize: 11, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 16 }}>
                Why teams switch
              </div>
              <h2 style={{ fontSize: mobile ? 32 : 44, fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px' }}>
                Less chaos. Less guesswork. Less downtime.
              </h2>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', margin: 0, maxWidth: 680, marginInline: 'auto' }}>
                {BRAND_NAME} isn't software for software's sake — every feature exists to take a real headache off your plate.
              </p>
            </motion.div>
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)', gap: 18 }}>
              {[
                { icon: FiTrendingDown, accent: '#10b981', pain: 'Equipment fails without warning', gain: 'Slash unplanned downtime', text: 'Preventive schedules and adherence tracking catch the service before it becomes a breakdown that stops the line.' },
                { icon: FiTrendingUp, accent: '#f59e0b', pain: 'Half the day lost to paperwork', gain: 'Boost daily productivity', text: 'Techs stay on the tools, not buried in admin — mobile capture, task lists and one-tap time confirmations strip the busywork out of every job.' },
                { icon: FiAlertTriangle, accent: '#ef4444', pain: 'Hazards sit unreported', gain: 'Rectify issues & hazards fast', text: 'Anyone can raise a fault or safety hazard the instant they spot it, flag its priority, and track it through to a signed-off fix with a full photo trail.' },
                { icon: FiDollarSign, accent: '#eab308', pain: 'No idea what an asset really costs', gain: 'Control maintenance costs', text: 'Labour and material costs roll up by asset and functional location — see exactly where the money goes and budget with real numbers, not gut-feel.' },
                { icon: FiLayers, accent: '#3b82f6', pain: 'Three spreadsheets, none agree', gain: 'One source of truth', text: 'Planner, tracker and handover sheet collapse into one place your whole team actually trusts and opens every morning.' },
                { icon: FiShield, accent: '#8b5cf6', pain: '"Prove it was done"', gain: 'Audit-ready & compliant', text: 'Every work order, time confirmation, photo and material is logged against the asset with a permanent history trail.' },
                { icon: FiCheckCircle, accent: '#06b6d4', pain: 'Who was meant to do that?', gain: 'Accountability that sticks', text: 'Adherence and variance reporting show what was planned, who owned it, and what actually got finished this week.' },
                { icon: FiBarChart2, accent: '#ec4899', pain: 'Flying blind on the numbers', gain: 'See the whole operation', text: 'A live dashboard of remaining work, asset availability and monthly trends — run the shop on data instead of guesswork.' },
                { icon: FiZap, accent: '#22c55e', pain: 'Rollouts take months', gain: 'Live the same afternoon', text: 'Import your asset list, set frequencies, and your first weekly pre-read is ready to hand out at the toolbox meeting.' },
              ].map((o, i) => {
                const Icon = o.icon;
                return (
                  <motion.div key={o.gain}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: i * 0.06 }} whileHover={{ y: -4 }}
                    style={{ padding: '24px 22px', borderRadius: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ width: 42, height: 42, borderRadius: 11, background: `${o.accent}22`, border: `1px solid ${o.accent}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <Icon size={20} color={o.accent} />
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, textDecoration: 'line-through', textDecorationColor: 'rgba(239,68,68,0.5)' }}>{o.pain}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 8 }}>{o.gain}</div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{o.text}</div>
                  </motion.div>
                );
              })}
            </div>

            {/* Hosted-on-your-server trust strip */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.6 }}
              style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: mobile ? 12 : 18, flexWrap: 'wrap', justifyContent: 'center', textAlign: mobile ? 'center' : 'left', padding: mobile ? '18px 18px' : '20px 28px', borderRadius: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, flexShrink: 0, background: 'rgba(59,130,246,0.18)', border: '1px solid rgba(59,130,246,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiServer size={22} color="#60a5fa" />
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', marginBottom: 2 }}>Your data, on a server we run for you</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>Dedicated tenancy with its own database — not a shared pool. Hosted, backed up and kept up to date so your team just logs in and works.</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: '#34d399' }}>
                <FiLock size={14} /> Isolated per customer
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── BENEFIT CARDS ─────────────────────────────────────────── */}
        <section style={{ padding: mobile ? '36px 16px' : '56px 32px' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', marginBottom: 32 }}>
              <h2 style={{ fontSize: mobile ? 32 : 44, fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px' }}>
                Everything a maintenance team needs
              </h2>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', margin: 0 }}>
                Six core capabilities, dozens of details — built around how the work actually flows.
              </p>
            </motion.div>
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)', gap: 18 }}>
              {benefits.map((b, i) => {
                const Icon = b.icon;
                const accent = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'][i];
                return (
                  <motion.div key={b.title}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    whileHover={{ y: -4 }}
                    style={{ padding: '24px 22px', borderRadius: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', transition: 'background 0.2s' }}>
                    <div style={{ width: 42, height: 42, borderRadius: 11, background: `${accent}22`, border: `1px solid ${accent}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <Icon size={20} color={accent} />
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 6 }}>{b.title}</div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{b.text}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── WORK ORDER TYPES ──────────────────────────────────────── */}
        <section style={{ padding: mobile ? '36px 16px' : '56px 32px' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', marginBottom: 32 }}>
              <h2 style={{ fontSize: mobile ? 32 : 44, fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px' }}>
                Three work order types — one workflow
              </h2>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', margin: 0, maxWidth: 680, marginInline: 'auto' }}>
                Aligned with SAP PM conventions so plant maintenance teams feel at home from day one.
              </p>
            </motion.div>
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)', gap: 18 }}>
              {[
                {
                  code: 'PM01', tag: 'Breakdown',
                  icon: FiAlertTriangle, accent: '#ef4444',
                  title: 'Reactive maintenance',
                  text: 'Unplanned failures, urgent repairs, anything that stops production. Raised on the spot, prioritised against the running schedule.',
                  bullets: ['Fast capture from mobile', 'Auto-flagged in supervisor panel', 'Downtime hours counted'],
                },
                {
                  code: 'PM02', tag: 'Project',
                  icon: FiLayers, accent: '#f59e0b',
                  title: 'Project / improvement',
                  text: 'Scoped work and capital projects that span days or weeks. Excluded from "remaining" counts so they don\'t skew the daily picture.',
                  bullets: ['Multi-stage with sub-WOs', 'Materials and PO tracking', 'Hidden from weekly pre-read'],
                },
                {
                  code: 'PM03', tag: 'Preventive',
                  icon: FiCalendar, accent: '#10b981',
                  title: 'Preventive / scheduled',
                  text: 'Recurring services driven by templates and frequencies. The system creates them, plans them, and keeps the cycle on rails.',
                  bullets: ['Template-driven recurrence', 'Auto-rolls into pre-read', 'Adherence + variance tracked'],
                },
              ].map((w, i) => {
                const Icon = w.icon;
                return (
                  <motion.div key={w.code}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    style={{ position: 'relative', padding: '28px 24px', borderRadius: 16, background: 'rgba(255,255,255,0.04)', border: `1px solid ${w.accent}33`, overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: `radial-gradient(circle, ${w.accent}22 0%, transparent 70%)` }} />
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <div style={{ width: 46, height: 46, borderRadius: 12, background: `${w.accent}22`, border: `1px solid ${w.accent}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon size={22} color={w.accent} />
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 800, padding: '4px 10px', borderRadius: 999, background: `${w.accent}22`, color: w.accent, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{w.tag}</span>
                    </div>
                    <div style={{ position: 'relative', fontSize: 22, fontWeight: 900, color: w.accent, letterSpacing: '0.5px', marginBottom: 4 }}>{w.code}</div>
                    <div style={{ position: 'relative', fontSize: 17, fontWeight: 800, color: '#fff', marginBottom: 10 }}>{w.title}</div>
                    <div style={{ position: 'relative', fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, marginBottom: 16 }}>{w.text}</div>
                    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {w.bullets.map(b => (
                        <div key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                          <FiCheck size={12} color={w.accent} style={{ flexShrink: 0, marginTop: 3 }} />
                          <span>{b}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── WORKFLOW: CREATE → TECO ───────────────────────────────── */}
        <section style={{ padding: mobile ? '36px 16px' : '56px 32px' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', marginBottom: 32 }}>
              <h2 style={{ fontSize: mobile ? 32 : 44, fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px' }}>
                From raise to TECO — one workflow
              </h2>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', margin: 0, maxWidth: 680, marginInline: 'auto' }}>
                Every job follows the same six steps, whether it's a five-minute fix or a six-week shutdown.
              </p>
            </motion.div>
            <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(6, 1fr)', gap: mobile ? 14 : 12 }}>
              {/* Connector line — desktop only */}
              {!mobile && (
                <div style={{ position: 'absolute', top: 26, left: '8%', right: '8%', height: 2, background: 'linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899, #f59e0b, #10b981)', opacity: 0.3 }} />
              )}
              {[
                { n: 1, icon: FiEdit3, color: '#3b82f6', title: 'Raise', text: 'Create from a template, asset, or blank. PM03 auto-spawns from frequencies.' },
                { n: 2, icon: FiCalendar, color: '#8b5cf6', title: 'Plan', text: 'Drop into the weekly pre-read, watch capacity bars fill per technician.' },
                { n: 3, icon: FiPackage, color: '#ec4899', title: 'Order', text: 'Add materials, raise POs, track delivery — all on the WO row.' },
                { n: 4, icon: FiPlayCircle, color: '#f59e0b', title: 'Execute', text: 'Tech opens it on Android, takes photos, logs time and sub-WOs.' },
                { n: 5, icon: FiCheckCircle, color: '#06b6d4', title: 'Confirm', text: 'Final time confirmations, materials check, supervisor review.' },
                { n: 6, icon: FiShield, color: '#10b981', title: 'TECO', text: 'Supervisor signs off, costs roll up, history logged on the asset.' },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div key={s.n}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    style={{ position: 'relative', textAlign: 'center', padding: '12px 8px' }}>
                    <div style={{ position: 'relative', width: 54, height: 54, margin: '0 auto 14px', borderRadius: '50%', background: '#0f172a', border: `2px solid ${s.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 24px ${s.color}55` }}>
                      <Icon size={22} color={s.color} />
                      <div style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', background: s.color, color: '#fff', fontSize: 11, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.n}</div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', marginBottom: 6 }}>{s.title}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{s.text}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── DETAILED LIFECYCLE: notification → TECO → cost ────────── */}
        <section style={{ padding: mobile ? '36px 16px' : '56px 32px' }}>
          <div style={{ maxWidth: 920, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', marginBottom: 40 }}>
              <div style={{ display: 'inline-block', padding: '5px 12px', borderRadius: 999, background: 'rgba(139,92,246,0.14)', border: '1px solid rgba(139,92,246,0.35)', color: '#a78bfa', fontSize: 11, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 16 }}>
                The whole journey
              </div>
              <h2 style={{ fontSize: mobile ? 30 : 44, fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px', lineHeight: 1.12 }}>
                The life of a work order — in detail
              </h2>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', margin: 0, maxWidth: 680, marginInline: 'auto' }}>
                From the notification that lands on a technician's phone to the cost that rolls up the asset tree. Here's exactly what happens, and what the system handles for you.
              </p>
            </motion.div>

            <div style={{ position: 'relative' }}>
              {/* vertical rail */}
              <div style={{ position: 'absolute', left: mobile ? 21 : 27, top: 10, bottom: 10, width: 2, background: 'linear-gradient(to bottom, #3b82f6, #8b5cf6, #ec4899, #f59e0b, #06b6d4, #10b981, #22c55e)', opacity: 0.35 }} />
              {[
                { icon: FiAlertTriangle, color: '#ef4444', stage: 'Issue spotted', text: 'A technician or operator notices a fault, breakdown or safety hazard out in the field and raises a work order on the spot from their phone — tag the asset, snap a photo, set the priority. Nothing waits for a trip back to the office.', auto: 'Auto: capture an issue against any asset in seconds.' },
                { icon: FiBell, color: '#3b82f6', stage: 'Notified', text: 'The moment the job is assigned, the right person gets a push notification and an email — but only when it\'s due this week, so inboxes stay calm. Recurring PM03 services raise and assign themselves on their schedule, so routine work never slips.', auto: 'Auto: assignee notified; PM03 jobs self-raise on frequency.' },
                { icon: FiCalendar, color: '#8b5cf6', stage: 'Planned into the week', text: 'Supervisors drag the job into a day on the weekly pre-read. Capacity bars fill per technician so nobody gets overloaded, and the frozen plan exports to a PDF for the toolbox meeting.', auto: 'Auto: capacity totals recalculate as you drag.' },
                { icon: FiPackage, color: '#ec4899', stage: 'Materials ordered', text: 'Parts, services and external labour are added to the work order. Raise a PO, mark it ordered, then delivered — every step tracked on the row. A weekly reminder flags anything still missing before the job is due.', auto: 'Auto: pending-materials reminder for next week\'s jobs.' },
                { icon: FiPlayCircle, color: '#f59e0b', stage: 'Executed in the field', text: 'The technician opens the WO on Android, works the task list, takes photos straight from the camera, and logs sub-WOs and operations as the job unfolds — no trip back to the office.', auto: 'Auto: photos upload against the WO and asset.' },
                { icon: FiClock, color: '#06b6d4', stage: 'Time confirmed', text: 'Each block of work is confirmed with hours and minutes against an operation. The labour cost is calculated automatically from the confirmed time and the worker\'s hourly rate — no timesheets to reconcile.', auto: 'Auto: labour cost = confirmed time × rate.' },
                { icon: FiShield, color: '#10b981', stage: 'Reviewed & signed off (TECO)', text: 'The supervisor reviews time, materials and task completion, then TECOs the work order. Where the job is recurring, the next instance is scheduled automatically so the cycle never drops.', auto: 'Auto: next PM03 occurrence is scheduled on TECO.' },
                { icon: FiTrendingUp, color: '#22c55e', stage: 'Costs roll up', text: 'Labour and materials post to the asset, then roll up the functional-location tree. Lifetime cost on a site includes every sub-asset beneath it — caravan, generator, spark plug — with zero manual tallying.', auto: 'Auto: costs aggregate by asset and location.' },
              ].map((s, i, arr) => {
                const Icon = s.icon;
                return (
                  <motion.div key={s.stage}
                    initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    style={{ position: 'relative', display: 'flex', gap: mobile ? 16 : 22, paddingBottom: i === arr.length - 1 ? 0 : 26 }}>
                    <div style={{ flexShrink: 0, width: mobile ? 44 : 56, height: mobile ? 44 : 56, borderRadius: '50%', background: '#0f172a', border: `2px solid ${s.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 22px ${s.color}55`, zIndex: 1 }}>
                      <Icon size={mobile ? 18 : 24} color={s.color} />
                    </div>
                    <div style={{ flex: 1, paddingTop: mobile ? 2 : 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 900, color: s.color }}>STEP {i + 1}</span>
                        <span style={{ fontSize: mobile ? 17 : 19, fontWeight: 800, color: '#fff' }}>{s.stage}</span>
                      </div>
                      <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: 8 }}>{s.text}</div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: s.color, background: `${s.color}14`, border: `1px solid ${s.color}33`, padding: '5px 11px', borderRadius: 999 }}>
                        <FiZap size={11} /> {s.auto}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── COST TRACKING DEEP-DIVE ───────────────────────────────── */}
        <section style={{ padding: mobile ? '36px 16px' : '56px 32px' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1.1fr', gap: mobile ? 28 : 56, alignItems: 'center' }}>
            {/* Left — cost breakdown visual */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }}
              style={{ borderRadius: 16, background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 30px 80px rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', padding: 22 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Lifetime cost · functional location</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 18 }}>
                <span style={{ fontSize: 34, fontWeight: 900, color: '#fff' }}>$<Counter to={48270} /></span>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>incl. all sub-assets</span>
              </div>
              {[
                { label: 'Labour', detail: 'confirmed time × rate', val: 60, amount: '$28,960', color: '#06b6d4' },
                { label: 'Materials & parts', detail: 'with PO tracking', val: 31, amount: '$14,910', color: '#ec4899' },
                { label: 'Services / ext. labour', detail: 'contractor invoices', val: 9, amount: '$4,400', color: '#f59e0b' },
              ].map((r) => (
                <div key={r.label} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{r.label} <span style={{ fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.45)' }}>· {r.detail}</span></span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: r.color }}>{r.amount}</span>
                  </div>
                  <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${r.val}%` }} viewport={{ once: true }} transition={{ duration: 1, ease: 'easeOut' }}
                      style={{ height: '100%', background: r.color, borderRadius: 4 }} />
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['Caravan — CV-1', 'Generator — CV-1.01', 'Spark plug — CV-1.01.01'].map((a, i) => (
                  <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: i * 16, fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                    <FiGitMerge size={12} color="#10b981" />
                    <span style={{ fontFamily: 'monospace' }}>{a}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>{['$31,200', '$14,070', '$3,000'][i]}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right — explanation */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: 0.1 }}>
              <div style={{ display: 'inline-block', padding: '5px 12px', borderRadius: 999, background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.35)', color: '#fbbf24', fontSize: 11, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 16 }}>
                Cost tracking
              </div>
              <h2 style={{ fontSize: mobile ? 30 : 42, fontWeight: 900, letterSpacing: '-1px', margin: '0 0 16px', lineHeight: 1.12 }}>
                Know what every asset really costs
              </h2>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', margin: '0 0 24px', lineHeight: 1.6 }}>
                Costs aren't something you type in at the end — they accrue automatically as the work happens, and they roll all the way up the location tree.
              </p>
              {[
                { icon: FiClock, color: '#06b6d4', title: 'Labour, costed automatically', text: 'Every time confirmation multiplies hours by the worker\'s rate. No timesheets, no spreadsheets — the labour figure is always current.' },
                { icon: FiPackage, color: '#ec4899', title: 'Materials with PO tracking', text: 'Parts, services and external labour each carry a cost and a purchase-order trail from ordered to delivered.' },
                { icon: FiGitMerge, color: '#10b981', title: 'Roll-up by asset & location', text: 'A sub-asset\'s cost counts toward its parent and every functional location above it — lifetime cost on a site is the true all-in number.' },
                { icon: FiBarChart2, color: '#f59e0b', title: 'Any period, any node', text: 'Filter a roll-up to year-to-date or a custom range, alongside work-order counts and downtime hours for the same window.' },
              ].map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
                    <div style={{ flexShrink: 0, width: 38, height: 38, borderRadius: 10, background: `${c.color}1f`, border: `1px solid ${c.color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={18} color={c.color} />
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', marginBottom: 3 }}>{c.title}</div>
                      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{c.text}</div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* ── FULL FEATURE LIST ─────────────────────────────────────── */}
        <section style={{ padding: mobile ? '36px 16px' : '56px 32px' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', marginBottom: 32 }}>
              <h2 style={{ fontSize: mobile ? 32 : 44, fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px' }}>
                Every feature, where you'd expect it
              </h2>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', margin: 0, maxWidth: 680, marginInline: 'auto' }}>
                Six categories, built around the way maintenance teams actually work — not a checkbox list.
              </p>
            </motion.div>
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)', gap: 18 }}>
              {[
                {
                  icon: FiClipboard, accent: '#3b82f6', title: 'Work order management',
                  items: ['PM01 / PM02 / PM03 types', 'Sub-WOs and operations', 'Recurring schedules from templates', 'Sequence-mode templates', 'Asset-linked work history', 'Bulk import / export'],
                },
                {
                  icon: FiCalendar, accent: '#8b5cf6', title: 'Planning & scheduling',
                  items: ['Weekly pre-read with drag-and-drop', 'Per-technician capacity bars', 'Date-clamped to displayed week', 'PDF export of frozen pre-read', 'Available jobs panel', 'Team overdue tracking'],
                },
                {
                  icon: FiGitMerge, accent: '#10b981', title: 'Assets & functional locations',
                  items: ['SAP-style functional location tree', 'Sub-assets nested under any asset', 'Cost roll-up by asset & location', 'Full asset history + audit trail', 'Photos and document attachments', 'Active-assets gauge + monthly trend'],
                },
                {
                  icon: FiSmartphone, accent: '#f59e0b', title: 'Mobile & field',
                  items: ['Native Android wrapper', 'Direct camera capture from any field', 'Offline-tolerant photo upload', 'Time confirmations on the row', 'TECO from the workshop floor', 'Push notifications'],
                },
                {
                  icon: FiBarChart2, accent: '#ec4899', title: 'Reporting & insights',
                  items: ['Live dashboard with HelpTips', 'Monthly history of remaining WOs', 'Asset availability history', 'Insights charts in dd/mm/yyyy', 'PDF reports and exports', 'Adherence and variance metrics'],
                },
                {
                  icon: FiUsers, accent: '#06b6d4', title: 'Admin & access',
                  items: ['Granular role permissions', 'Supervisor + admin panels', 'Credentials vault', 'Projects with linked WOs', 'Custom branding per tenant', 'Email + push notifications'],
                },
              ].map((cat, i) => {
                const Icon = cat.icon;
                return (
                  <motion.div key={cat.title}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    style={{ padding: '24px 26px', borderRadius: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 10, background: `${cat.accent}22`, border: `1px solid ${cat.accent}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon size={18} color={cat.accent} />
                      </div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: '#fff' }}>{cat.title}</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 14px' }}>
                      {cat.items.map(it => (
                        <div key={it} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5, color: 'rgba(255,255,255,0.78)', lineHeight: 1.45 }}>
                          <FiCheck size={11} color={cat.accent} style={{ flexShrink: 0, marginTop: 4 }} />
                          <span>{it}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── REVIEWS ───────────────────────────────────────────────── */}
        <section style={{ padding: mobile ? '36px 16px' : '56px 32px' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', marginBottom: 32 }}>
              <h2 style={{ fontSize: mobile ? 32 : 44, fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px' }}>
                Trusted on the workshop floor
              </h2>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', margin: 0 }}>
                Real customers, real results.
              </p>
            </motion.div>
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)', gap: 20 }}>
              {reviews.map((r, i) => {
                const initials = r.name.split(/\s+/).map(p => p[0]).slice(0, 2).join('');
                const avatarColors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];
                return (
                  <motion.div key={r.name}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    style={{ padding: 28, borderRadius: 16, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
                      {[...Array(5)].map((_, s) => <FiStar key={s} size={14} fill="#fbbf24" color="#fbbf24" />)}
                    </div>
                    <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.92)', lineHeight: 1.55, margin: '0 0 22px', fontWeight: 400 }}>
                      &ldquo;{r.text}&rdquo;
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 44, height: 44, borderRadius: '50%', background: `linear-gradient(135deg, ${avatarColors[i % avatarColors.length]}, ${avatarColors[(i + 1) % avatarColors.length]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                        {initials}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{r.name}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>{r.role} · {r.company}</div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ─────────────────────────────────────────────── */}
        <section style={{ padding: mobile ? '40px 16px 24px' : '56px 32px 32px' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', padding: mobile ? '32px 20px' : '48px 32px', borderRadius: 20, background: 'linear-gradient(135deg, rgba(37,99,235,0.18), rgba(124,58,237,0.18))', border: '1px solid rgba(255,255,255,0.12)' }}>
              <h3 style={{ fontSize: mobile ? 24 : 32, fontWeight: 900, letterSpacing: '-0.8px', margin: '0 0 10px' }}>
                Ready to give your supervisors their week back?
              </h3>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)', margin: '0 0 24px' }}>
                It takes about ten minutes to import your asset list and start scheduling.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                  onClick={() => navigate(ctaPrimary)}
                  style={{ padding: '14px 30px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 10px 30px rgba(37,99,235,0.45)' }}>
                  {user ? 'Open App' : 'Start free trial'} <FiArrowRight size={15} />
                </motion.button>
                <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                  onClick={() => window.location.href = '/api/download/android'}
                  style={{ padding: '14px 24px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiDownload size={14} /> Get the Android app
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── FOOTER ────────────────────────────────────────────────── */}
        <footer style={{ padding: mobile ? '24px 16px 36px' : '32px 32px 48px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Logo size={20} light />
              <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>{BRAND_NAME}</span>
              <span>·</span>
              <span>v{APP_VERSION}</span>
              <span>·</span>
              <span>© {COPYRIGHT_YEAR}</span>
            </div>
            <div>
              Powered by <a href={PARENT_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontWeight: 600 }}>{PARENT_NAME}</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
